import { hash64, type Key } from "./hashing";

// Largest Mersenne prime below 2^62; the hash family works in the field Z_p.
const PRIME = (1n << 61n) - 1n;

export interface MinHashOptions {
  /** Number of hash functions in the signature (more → lower variance). */
  numHashes?: number;
  /**
   * Seed for the hash family. Two MinHash instances can only be compared if
   * they share the same seed (hence the same family), so this defaults to 0.
   */
  seed?: number;
}

/**
 * MinHash estimates the Jaccard similarity |A ∩ B| / |A ∪ B| of two sets from
 * fixed-size signatures, without ever materialising the sets together.
 *
 * For each of k hash functions, a set's signature stores the minimum hash value
 * over the set's elements. The probability that two sets share the same minimum
 * for a given hash function is exactly their Jaccard similarity, so the fraction
 * of matching signature slots is an unbiased estimate of it.
 *
 * We use a single 64-bit base hash per element and derive k hashes cheaply as
 * (a_i·h + b_i) mod p, a standard universal family.
 */
export class MinHash {
  readonly numHashes: number;
  readonly seed: number;
  readonly signature: BigUint64Array;
  private readonly a: bigint[];
  private readonly b: bigint[];

  constructor(options: MinHashOptions = {}) {
    const { numHashes = 128, seed = 0 } = options;
    if (numHashes <= 0) throw new RangeError("numHashes must be positive");
    this.numHashes = numHashes;
    this.seed = seed;
    this.signature = new BigUint64Array(numHashes).fill(PRIME);
    this.a = new Array(numHashes);
    this.b = new Array(numHashes);
    for (let i = 0; i < numHashes; i++) {
      // Deterministic per-index coefficients in [1, p) and [0, p) so every
      // instance with the same seed uses an identical hash family.
      this.a[i] = (hash64(`minhash:a:${i}`, seed) % (PRIME - 1n)) + 1n;
      this.b[i] = hash64(`minhash:b:${i}`, seed) % PRIME;
    }
  }

  /** Add one element of the set this signature represents. */
  add(key: Key): void {
    const h = hash64(key, this.seed) % PRIME;
    for (let i = 0; i < this.numHashes; i++) {
      const v = (this.a[i] * h + this.b[i]) % PRIME;
      if (v < this.signature[i]) this.signature[i] = v;
    }
  }

  /** Add many elements at once. */
  addAll(keys: Iterable<Key>): void {
    for (const k of keys) this.add(k);
  }

  /** Estimated Jaccard similarity with another signature (same family). */
  jaccard(other: MinHash): number {
    if (other.numHashes !== this.numHashes || other.seed !== this.seed)
      throw new Error("MinHash signatures must share numHashes and seed to be compared");
    let equal = 0;
    for (let i = 0; i < this.numHashes; i++) {
      if (this.signature[i] === other.signature[i]) equal++;
    }
    return equal / this.numHashes;
  }

  /** Standard error of a Jaccard estimate at true similarity j. */
  standardError(j: number): number {
    return Math.sqrt((j * (1 - j)) / this.numHashes);
  }
}

export interface MinHashLSHOptions {
  /** Number of bands the signature is split into. */
  bands: number;
  /** Rows per band. bands·rows must equal the signature length. */
  rows: number;
}

/**
 * Locality-sensitive hashing over MinHash signatures for scalable
 * near-duplicate search. The signature is cut into `bands` slices of `rows`
 * rows; two items become *candidates* if any band matches exactly. Tuning bands
 * and rows sets the similarity threshold at roughly (1/bands)^(1/rows).
 */
export class MinHashLSH {
  readonly bands: number;
  readonly rows: number;
  private readonly buckets: Array<Map<string, Set<string>>>;
  private readonly stored = new Map<string, MinHash>();

  constructor(options: MinHashLSHOptions) {
    const { bands, rows } = options;
    if (bands <= 0 || rows <= 0) throw new RangeError("bands and rows must be positive");
    this.bands = bands;
    this.rows = rows;
    this.buckets = Array.from({ length: bands }, () => new Map());
  }

  /** The similarity threshold this configuration is tuned for. */
  threshold(): number {
    return Math.pow(1 / this.bands, 1 / this.rows);
  }

  private bandKeys(mh: MinHash): string[] {
    if (mh.numHashes !== this.bands * this.rows)
      throw new Error("signature length must equal bands*rows");
    const keys: string[] = [];
    for (let band = 0; band < this.bands; band++) {
      let s = "";
      for (let r = 0; r < this.rows; r++) s += mh.signature[band * this.rows + r].toString(36) + ",";
      keys.push(s);
    }
    return keys;
  }

  insert(id: string, mh: MinHash): void {
    this.stored.set(id, mh);
    const keys = this.bandKeys(mh);
    for (let band = 0; band < this.bands; band++) {
      const map = this.buckets[band];
      let set = map.get(keys[band]);
      if (!set) map.set(keys[band], (set = new Set()));
      set.add(id);
    }
  }

  /** Candidate ids sharing at least one band with the query (excludes itself). */
  query(mh: MinHash, excludeId?: string): string[] {
    const keys = this.bandKeys(mh);
    const candidates = new Set<string>();
    for (let band = 0; band < this.bands; band++) {
      const set = this.buckets[band].get(keys[band]);
      if (set) for (const id of set) if (id !== excludeId) candidates.add(id);
    }
    return [...candidates];
  }
}
